import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Shield, Search, Download, Filter, RefreshCw, Loader2, CheckCircle2, X, Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import api from '../lib/api';

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function PermissionsAuditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPerm, setFilterPerm] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [detailUser, setDetailUser] = useState(null);

  const reload = useCallback(async (perm = '') => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/admin/permissions-audit', {
        params: perm ? { permission: perm } : {},
      });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load permissions audit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filteredUsers = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.users.filter(u => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (s && !u.email?.toLowerCase().includes(s) && !u.name?.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [data, search, roleFilter]);

  const exportCsv = () => {
    if (!filteredUsers.length) return;
    const cols = ['Email', 'Name', 'Role', 'Active', 'Permission Count', 'Custom Permissions', 'All Permissions', 'Last Login'];
    const lines = [cols.join(',')];
    filteredUsers.forEach(u => {
      lines.push([
        csvEscape(u.email),
        csvEscape(u.name),
        csvEscape(u.role_label),
        u.active ? 'Yes' : 'No',
        u.permissions_count,
        csvEscape((u.custom_permissions || []).join('; ')),
        csvEscape((u.permissions || []).join('; ')),
        csvEscape(u.last_login || ''),
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `permissions-audit-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading audit…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-5" data-testid="permissions-audit-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-7 w-7 text-emerald-600" />
            Permission Audit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Who has what — across roles and custom permissions. Useful for SOC-2 / HIPAA reviews.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => reload(filterPerm)} className="gap-1.5" data-testid="audit-refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filteredUsers.length} className="gap-1.5" data-testid="audit-export-csv-btn">
            <Download className="h-4 w-4" /> Download CSV
          </Button>
        </div>
      </header>

      {/* Filters */}
      <div className="grid md:grid-cols-3 gap-3 rounded-xl border bg-card p-4">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Search className="h-3 w-3" /> Search user
          </label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Email or name…"
            className="h-9"
            data-testid="audit-search-input"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Filter className="h-3 w-3" /> Filter by role
          </label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-9" data-testid="audit-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {data.roles.map(r => (
                <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Shield className="h-3 w-3" /> Who has permission…
          </label>
          <div className="flex gap-1">
            <Select value={filterPerm || 'none'} onValueChange={(v) => { const p = v === 'none' ? '' : v; setFilterPerm(p); reload(p); }}>
              <SelectTrigger className="h-9" data-testid="audit-perm-filter">
                <SelectValue placeholder="Pick a permission" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="none">— None —</SelectItem>
                {data.permissions.map(p => (
                  <SelectItem key={p.key} value={p.key}>
                    <span className="text-xs text-muted-foreground capitalize mr-1.5">{p.category}</span>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterPerm && (
              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => { setFilterPerm(''); reload(''); }} data-testid="audit-perm-filter-clear">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Users" value={data.user_count} icon={Users} tone="emerald" />
        <Tile label="Roles" value={data.roles.length} icon={Shield} tone="sky" />
        <Tile label="Permissions" value={data.permissions.length} icon={CheckCircle2} tone="amber" />
        <Tile label="Filtered" value={filteredUsers.length} icon={Filter} tone={filteredUsers.length === data.user_count ? 'slate' : 'rose'} />
      </div>

      {/* User table */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase font-semibold tracking-wide text-muted-foreground border-b bg-muted/30">
          <div className="col-span-3">User</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-3">Custom permissions</div>
          <div className="col-span-1 text-right">Total perms</div>
          <div className="col-span-2">Last login</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {filteredUsers.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground text-sm">
            {filterPerm ? 'No users match this permission with current filters.' : 'No users match the current filters.'}
          </div>
        ) : (
          <div className="divide-y max-h-[60vh] overflow-y-auto">
            {filteredUsers.map(u => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => setDetailUser(u)}
                className="w-full grid grid-cols-12 gap-2 px-4 py-3 text-sm text-left hover:bg-muted/30 transition-colors"
                data-testid={`audit-user-${u.user_id}`}
              >
                <div className="col-span-3 min-w-0">
                  <p className="font-medium truncate">{u.email}</p>
                  {u.name && <p className="text-[11px] text-muted-foreground truncate">{u.name}</p>}
                </div>
                <div className="col-span-2 text-xs">
                  <span className="inline-flex px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium">
                    {u.role_label}
                  </span>
                </div>
                <div className="col-span-3 min-w-0">
                  {(u.custom_permissions || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {(u.custom_permissions || []).slice(0, 3).map(p => (
                        <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
                          {p}
                        </span>
                      ))}
                      {u.custom_permissions.length > 3 && (
                        <span className="text-[10px] text-muted-foreground">+{u.custom_permissions.length - 3}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-[11px] text-muted-foreground italic">— inherits from role —</span>
                  )}
                </div>
                <div className="col-span-1 text-right tabular-nums font-semibold">{u.permissions_count}</div>
                <div className="col-span-2 text-[11px] text-muted-foreground tabular-nums">
                  {u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}
                </div>
                <div className="col-span-1 text-right">
                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                    u.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {u.active ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detailUser} onOpenChange={(o) => !o && setDetailUser(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-user-detail">
          <DialogHeader>
            <DialogTitle>{detailUser?.email}</DialogTitle>
            <DialogDescription>
              Role: <span className="font-semibold">{detailUser?.role_label}</span> ·
              Total permissions: <span className="font-semibold">{detailUser?.permissions_count}</span>
            </DialogDescription>
          </DialogHeader>
          {detailUser && (
            <PermissionMatrix
              userPerms={new Set(detailUser.permissions || [])}
              custom={new Set(detailUser.custom_permissions || [])}
              catalogue={data.permissions}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermissionMatrix({ userPerms, custom, catalogue }) {
  const grouped = useMemo(() => {
    const m = {};
    catalogue.forEach(p => {
      if (!m[p.category]) m[p.category] = [];
      m[p.category].push(p);
    });
    return m;
  }, [catalogue]);

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([cat, perms]) => (
        <div key={cat}>
          <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5 capitalize">{cat}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {perms.map(p => {
              const has = userPerms.has(p.key);
              const isCustom = custom.has(p.key);
              return (
                <div
                  key={p.key}
                  className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                    has ? (isCustom ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50') : 'border-border bg-card opacity-50'
                  }`}
                >
                  <CheckCircle2 className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${has ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                  <div className="min-w-0">
                    <p className={`font-medium ${has ? '' : 'line-through'}`}>{p.label}</p>
                    {isCustom && <p className="text-[9px] uppercase tracking-wide font-bold text-amber-700">Custom-granted</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Tile({ label, value, icon: Icon, tone }) {
  const TONE = {
    slate:   'border-slate-200 bg-slate-50 text-slate-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    sky:     'border-sky-200 bg-sky-50 text-sky-800',
    amber:   'border-amber-200 bg-amber-50 text-amber-800',
    rose:    'border-rose-200 bg-rose-50 text-rose-800',
  };
  return (
    <div className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${TONE[tone] || TONE.slate}`}>
      <Icon className="h-5 w-5 opacity-70" />
      <div>
        <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
        <p className="text-2xl font-bold tabular-nums leading-none">{value}</p>
      </div>
    </div>
  );
}
